import { Company, Job, Readable } from 'generated/apolloComponents'
import React, { useEffect, useReducer, useRef, useState } from 'react'
import useTranslation from 'next-translate/useTranslation'
import dynamic from 'next/dynamic'
import Link from 'next/link'

import { ChevronDownIcon } from '@heroicons/react/24/solid'
import {
  BriefcaseIcon,
  InformationCircleIcon,
  MapPinIcon,
  NewspaperIcon,
  CameraIcon,
} from '@heroicons/react/24/outline'
import BenefitsList from '@/components/molecules/BenefitsList'
import {
  getTools,
  getWorkFlow,
  getWorkFlowManage,
  isEmpty,
  sortTechIcon,
} from '@/helpers/utils'
import ListCheck from '@/components/atoms/ListCheck'

import styles from './Company.module.scss'
import ArticlePreview from '@/components/organisms/ArticlePreview/ArticlePreview'
import CompanyJobsList from '../CompanyJobsList'
import {
  initialJobListState,
  JobListContext,
  jobListReducer,
} from '@/context/jobListContext'
import Button from '@/components/atoms/Button'
import SocialMediaPanel from '@/components/organisms/Company/SocialMediaPanel'
import TechIcon from '@/components/atoms/TechIcon'
import CompanyGallery from '@/components/organisms/Company/CompanyGallery'

import {
  BasicLayout,
  BlankLayout,
  BrandLayout,
  ProfessionalLayout,
} from './Layouts'
import Breadcrumbs from '@/components/atoms/Breadcrumbs'
import useWindowWidth from '@/hooks/useWindowWidth'

const Offices = dynamic(
  () => import('@/components/organisms/Company/Offices'),
  { ssr: false }
)

interface IProps {
  company: Company
  articles: Readable[]
  jobsLocations: string[]
  jobs: Job[]
  totalJobsCount: number
  domainLang: string
  refs?: any
}
type BCItem = {
  title: string
  url?: string
}
const CompanyTemplate = ({
  company,
  articles,
  jobsLocations,
  jobs,
  domainLang,
  totalJobsCount,
  refs,
}: IProps) => {
  const [jobListData, setJobListData] = useState(jobs)
  const { t } = useTranslation('company')
  const [state, dispatch] = useReducer(jobListReducer, initialJobListState)
  const [emptyCover, setEmptyCover] = useState<boolean>(false)
  const [isHistoryExpanded, setIsHistoryExpanded] = useState(false)
  const width = useWindowWidth()

  const DynamicOtherJobsList = dynamic(() => import('../OtherJobsList'), {
    ssr: false,
  })

  const sectionRefs = [
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
  ]

  const layoutLevelAtLeast = (level: string) => {
    const levels = ['blank', 'basic', 'brand', 'master', 'custom']

    return (
      levels.indexOf(level) <= levels.indexOf(company.currentLayout as string)
    )
  }

  const menu = () => {
    const tmpMenu = []
    company.history
      ? tmpMenu.push({
          icon: <InformationCircleIcon />,
          title: t('company:about_company'),
          url: '#about',
          slug: 'about',
        })
      : tmpMenu.push({})
    company.galleryAssets?.length
      ? tmpMenu.push({
          icon: <CameraIcon />,
          title: t('images_videos'),
          url: '#gallery',
          slug: 'gallery',
        })
      : tmpMenu.push({})
    company.offices
      ? tmpMenu.push({
          icon: <MapPinIcon />,
          title: `${t('locations')} <span class="opacity-60">(${
            company.offices.length
          })</span>`,
          url: '#locations',
          slug: 'locations',
        })
      : tmpMenu.push({})
    articles.length
      ? tmpMenu.push({
          icon: <NewspaperIcon />,
          title: `${t('readme')} <span class="opacity-60">(${
            articles.length
          })</span>`,
          url: '#readme',
          slug: 'readme',
        })
      : tmpMenu.push({})
    tmpMenu.push({
      icon: <BriefcaseIcon />,
      title: `${t('job_offers')} <span class="opacity-60">(${
        company.activeOffersCount
      })</span>`,
      url: '#offers',
      slug: 'offers',
    })

    return tmpMenu
  }
  useEffect(() => {
    company.coverPhoto?.url && company.coverPhoto?.url.includes('missing')
      ? setEmptyCover(true)
      : setEmptyCover(false)
  }, [])

  const isExpandHistoryVisible =
    company.history &&
    company.history.length > 380 &&
    !isHistoryExpanded &&
    width &&
    width < 600

  const breadcrumbs: BCItem[] = [
    {
      title: t('employers'),
      url: '/companies/profiles',
    },
  ]
  const filtered = breadcrumbs.filter((value) => value.url)
  filtered.push({ title: company.name })

  const color =
    (layoutLevelAtLeast('master') && company.themeColor) || '#726EFF'

  const breadcrumbsBgColor = color && !emptyCover ? color : '#eff5f5'

  const breadcrumbsHexToRgb = breadcrumbsBgColor.slice(1).match(/.{1,2}/g)

  const breadcrumbsRgbColors = [
    parseInt(breadcrumbsHexToRgb![0], 16),
    parseInt(breadcrumbsHexToRgb![1], 16),
    parseInt(breadcrumbsHexToRgb![2], 16),
  ]

  let breadcrumbsColor

  if (
    breadcrumbsRgbColors[0] * 0.299 +
      breadcrumbsRgbColors[1] * 0.587 +
      breadcrumbsRgbColors[2] * 0.114 >
    186
  ) {
    breadcrumbsColor = '#000'
  } else {
    breadcrumbsColor = '#fff'
  }

  return (
    <div className='bg-gray-bg overflow-hidden md:overflow-visible'>
      <div
        style={{
          backgroundColor: breadcrumbsBgColor,
          color: breadcrumbsColor,
        }}
      >
        <Breadcrumbs data={filtered} className='py-5' />
      </div>
      {company.currentLayout === 'blank' && <BlankLayout company={company} />}
      {(company.currentLayout === 'custom' ||
        company.currentLayout === 'master') && (
        <ProfessionalLayout
          company={company}
          emptyCover={emptyCover}
          menu={menu()}
          navRefs={sectionRefs}
        />
      )}
      {company.currentLayout === 'brand' && (
        <BrandLayout company={company} menu={menu()} navRefs={sectionRefs} />
      )}
      {company.currentLayout === 'basic' && <BasicLayout company={company} />}

      <main className='bg-gray-bg py-8 overflow-hidden'>
        <div className='container'>
          {company.activeOffersCount !== 0 && (
            <Button
              href='#offers'
              type='primary'
              className='w-full mb-4 md:hidden'
            >
              {t('job_offers')}{' '}
              <span className='opacity-50 ml-2'>
                ({company.activeOffersCount})
              </span>
            </Button>
          )}

          {(company.currentLayout === 'custom' ||
            company.currentLayout === 'master') && (
            <div className='flex xs:flex-wrap md:flex-nowrap'>
              {company.history ? (
                <div
                  id='about'
                  className={`bg-white rounded-xl xs:w-full ${
                    company.facebook || company.twitter ? 'md:w-fit' : 'w-full'
                  } ${
                    company.facebook || company.twitter ? 'md:h-700' : 'h-full'
                  } px-6 md:px-10 py-6 md:py-10 overflow-auto`}
                  ref={sectionRefs[0]}
                >
                  <h2 className='text-2xl md:text-4xl font-medium mb-5'>
                    {t('company')}
                  </h2>
                  {isExpandHistoryVisible ? (
                    <div
                      className='content font-normal leading-normal'
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{
                        __html: company.history.slice(0, 380),
                      }}
                    />
                  ) : (
                    <div
                      className='content font-normal leading-normal'
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{ __html: company.history }}
                    />
                  )}

                  {company.history.length > 380 && width && width < 600 && (
                    <button
                      type='button'
                      className='flex items-center mx-auto'
                      onClick={() => setIsHistoryExpanded(!isHistoryExpanded)}
                    >
                      {isHistoryExpanded
                        ? t('collapse_desc')
                        : t('expand_desc')}
                      <ChevronDownIcon
                        style={{ width: '14px', marginLeft: '9px' }}
                      />
                    </button>
                  )}
                </div>
              ) : null}
              {company.facebook || company.twitter ? (
                <SocialMediaPanel
                  company={company}
                  color={color}
                  title={t('socials')}
                />
              ) : null}
            </div>
          )}

          {(company.currentLayout === 'basic' ||
            company.currentLayout === 'brand') &&
          company.history ? (
            <div
              id='about'
              className='bg-white rounded-xl px-6 md:px-10 py-6 md:py-10'
              ref={sectionRefs[0]}
            >
              <h2 className='text-2xl md:text-4xl font-medium mb-5'>
                {t('company')}
              </h2>
              <div
                className='content font-normal leading-normal'
                dangerouslySetInnerHTML={{ __html: company.history }}
              />
            </div>
          ) : null}

          {(company.currentLayout === 'basic' ||
            company.currentLayout === 'brand') &&
          company.skills ? (
            <div className='bg-white rounded-xl px-6 md:px-10 py-6 md:py-10 my-4'>
              <h2 className='text-2xl md:text-4xl font-medium mb-5'>
                {t('technological_stack')}
              </h2>
              {company.skills && (
                <div
                  className={` border-gray-100 w-f md:flex text-md py-3 border-b`}
                >
                  <div
                    className={`flex flex-wrap md:pl-3 ${styles['company__stack-data']}`}
                  >
                    <p className='md:pr-3 font-medium flex flex-wrap'>
                      {company.skills.map((item, index) => (
                        <span key={index} className='mr-4'>
                          {item}
                        </span>
                      ))}
                    </p>
                  </div>
                </div>
              )}
            </div>
          ) : null}

          {(company.techUsage?.lang?.length ||
            company.techUsage?.platform?.length ||
            company.techUsage?.tool?.length) &&
          layoutLevelAtLeast('master') ? (
            <div className='bg-white rounded-xl px-6 md:px-10 py-6 md:py-10 my-4'>
              <h2 className='text-2xl md:text-4xl font-medium mb-5'>
                {t('technological_stack')}
              </h2>
              {company.techUsage?.lang && company.techUsage?.lang.length > 0 && (
                <div
                  className={` border-gray-100 w-f md:flex text-md py-6 border-b`}
                >
                  <span
                    className={`font-medium text-gray-300 ${styles['company__stack-title']}`}
                  >
                    {t('lang_and_frameworks')}
                  </span>
                  <div
                    className={`flex flex-wrap md:pl-3 xs:pt-6 md:pt-0 ${styles['company__stack-data']}`}
                  >
                    <div className='md:pr-3 font-medium flex flex-wrap'>
                      {[...company.techUsage.lang]
                        .sort(sortTechIcon)
                        .map((item, index) => (
                          <span key={index} className='mr-4'>
                            <TechIcon item={item} color={color} />
                          </span>
                        ))}
                    </div>
                  </div>
                </div>
              )}
              {company.techUsage?.tool && company.techUsage?.tool.length > 0 && (
                <div
                  className={` border-gray-100 w-f md:flex text-md py-6 border-b`}
                >
                  <span
                    className={`font-medium text-gray-300 ${styles['company__stack-title']}`}
                  >
                    {t('tools')}
                  </span>
                  <div
                    className={`flex flex-wrap md:pl-3 xs:pt-6 md:pt-0 ${styles['company__stack-data']}`}
                  >
                    <div className='pr-3 font-medium flex flex-wrap'>
                      {[...company.techUsage?.tool]
                        .sort(sortTechIcon)
                        .map((item, index) => (
                          <span key={index} className='mr-4'>
                            <TechIcon item={item} color={color} />
                          </span>
                        ))}
                    </div>
                  </div>
                </div>
              )}
              {company.techUsage?.platform &&
                company.techUsage?.platform.length > 0 && (
                  <div className={` border-gray-100 w-f md:flex text-md py-6`}>
                    <span
                      className={`font-medium text-gray-300 ${styles['company__stack-title']}`}
                    >
                      {t('platforms')}
                    </span>
                    <div
                      className={`flex flex-wrap md:pl-3 xs:pt-6 md:pt-0 ${styles['company__stack-data']}`}
                    >
                      <span className='pr-3 font-medium flex flex-wrap'>
                        {[...company.techUsage?.platform]
                          .sort(sortTechIcon)
                          .map((item, index) => (
                            <span key={index} className='mr-4'>
                              <TechIcon item={item} color={color} />
                            </span>
                          ))}
                      </span>
                    </div>
                  </div>
                )}
            </div>
          ) : null}

          {company.employeeQuotes &&
          company.employeeQuotes.length !== 0 &&
          layoutLevelAtLeast('master') ? (
            <div className='bg-white rounded-xl px-6 px-10 py-6 md:py-10 my-4'>
              <h2 className='text-2xl md:text-4xl font-medium mb-6'>
                {t('our_team')}
              </h2>
              <div className='md:flex flex-wrap'>
                {company.employeeQuotes.slice(0, 8).map((person, index) => (
                  <div
                    key={index}
                    className={`sm:w-1/2 xs:w-full lg:w-1/4 items-center mb-8 md:mb-0 md:block text-center relative ${styles.company__person}`}
                  >
                    {person.photo?.url && (
                      <div className='md:w-full md:my-10 sm:w-1/4'>
                        <img
                          src={person.photo?.url}
                          className='rounded-lg max-w-48'
                          alt='person'
                        />
                      </div>
                    )}
                    <div className='my-3 text-left'>
                      <h3 className='font-medium text-2.5xl'>{person.name}</h3>
                      <p className='font-normal text-c22'>{person.position}</p>
                      {person.quote && (
                        <div
                          className='content font-normal leading-normal'
                          dangerouslySetInnerHTML={{ __html: person.quote }}
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {layoutLevelAtLeast('master') &&
          (!isEmpty(getWorkFlow(company)) || !isEmpty(getTools(company))) ? (
            <div className='bg-white rounded-xl px-6 md:px-10 py-6 md:py-10 my-4'>
              <h2 className='text-2xl md:text-4xl font-medium mb-6'>
                {t('how_we_work')}
              </h2>
              <div className='flex justify-between'>
                {(() => {
                  const flow: any = getWorkFlow(company)
                  const howWeFlow: any = getWorkFlowManage(company)
                  const tools: any = getTools(company)
                  return (
                    <div className='md:flex w-full flex-wrap'>
                      {flow.how_we_code && flow.how_we_code.length !== 0 && (
                        <div className='lg:w-1/2 w-full md:pr-4 mb-10 md:mb-0 mt-5'>
                          <h5 className='text-c22 font-medium mb-6'>
                            {t('work_with_code')}
                          </h5>
                          <ListCheck
                            data={flow.how_we_code}
                            langKey='code'
                            className='font-normal'
                            color={company.themeColor}
                          />
                        </div>
                      )}
                      {flow.how_we_test && flow.how_we_test.length !== 0 && (
                        <div className='lg:w-1/2 w-full md:pr-4 mb-10 md:mb-0 mt-5'>
                          <h5 className='text-c22 font-medium mb-6'>
                            {t('testing')}
                          </h5>
                          <ListCheck
                            data={flow.how_we_test}
                            langKey='test'
                            className='font-normal '
                            color={company.themeColor}
                          />
                        </div>
                      )}
                      {tools && tools.length !== 0 && (
                        <div className='lg:w-1/2 w-full md:pr-4 mb-10 md:mb-0 mt-5'>
                          <h5 className='text-c22 font-medium mb-6'>
                            {t('tools')}
                          </h5>
                          <ListCheck
                            data={tools}
                            langKey='tools'
                            className='font-normal'
                            color={company.themeColor}
                          />
                        </div>
                      )}
                      {howWeFlow.how_we_manage &&
                        howWeFlow.how_we_manage.length !== 0 && (
                          <div className='lg:w-1/2 w-full md:pr-4 mb-10 md:mb-0 mt-5'>
                            <h5 className='text-c22 font-medium mb-6'>
                              {t('project_management')}
                            </h5>
                            <ListCheck
                              data={howWeFlow.how_we_manage}
                              langKey='hwm'
                              className='font-normal'
                              color={company.themeColor}
                            />
                          </div>
                        )}
                    </div>
                  )
                })()}
              </div>
            </div>
          ) : null}

          {company.galleryAssets?.length && layoutLevelAtLeast('brand') ? (
            <section id='gallery' ref={sectionRefs[2]}>
              <CompanyGallery gallery={company.galleryAssets} />
            </section>
          ) : null}

          {company.benefits &&
          company.benefits.length !== 0 &&
          layoutLevelAtLeast('brand') ? (
            <div className='bg-white rounded-xl px-6 md:px-10 py-6 md:py-10 my-4'>
              <h2 className='text-2xl md:text-4xl font-medium mb-8 md:mb-16'>
                {t('benefits')}
              </h2>
              <div className='flex justify-between'>
                <div className='md:flex w-full'>
                  <BenefitsList
                    title={t('packages_and_funding')}
                    color={company.themeColor as string}
                    type='benefits'
                    benefits={company.benefits}
                    categories={['package', 'training', 'medical', 'financial']}
                    className='md:w-1/3'
                  />
                  <BenefitsList
                    color={company.themeColor as string}
                    title={t('relocation_package')}
                    type='relocation'
                    benefits={company.benefits}
                    categories={['relocation']}
                    className='md:w-1/3'
                  />
                  <BenefitsList
                    color={company.themeColor as string}
                    title={t('amenities')}
                    type='perks'
                    benefits={company.benefits}
                    categories={['kitchen', 'other', 'parking']}
                    className='md:w-1/3'
                  />
                </div>
              </div>
            </div>
          ) : null}

          {company.offices && company.offices.length !== 0 ? (
            <div
              id='locations'
              className='bg-white rounded-xl px-6 md:px-10 py-6 md:py-10 my-4'
              ref={sectionRefs[3]}
            >
              <Offices company={company} />
            </div>
          ) : null}
          {articles.length !== 0 ? (
            <div
              id='readme'
              className=' rounded-xl  md:px-10 md:py-10 mt-8 md:mt-0 md:my-12'
              ref={sectionRefs[4]}
            >
              <h2 className='text-2xl md:text-4xl font-medium'>
                {company.name} / {t('readme')}
              </h2>
              <div className='lg:flex flex-wrap lg:-mx-6 mt-4 md:mt-10'>
                {articles.slice(0, 3).map((item, index) => (
                  <ArticlePreview key={index} size='small' article={item} />
                ))}
              </div>
              {articles.length > 3 && (
                <Link
                  href={`/readme/companies/${company.id}`}
                  prefetch={false}
                  className='border right rounded-full py-2 px-4 float-right bg-white hover:bg-purple hover:border-purple hover:text-white w-full md:w-auto block md:inline-block text-center text-xl md:text-base'
                >
                  {t('common:read_more')}
                </Link>
              )}
            </div>
          ) : null}
          {totalJobsCount !== 0 && (
            <div id='offers' className='pt-6 mt-6' ref={sectionRefs[5]}>
              <h2 className='text-2xl md:text-4xl font-medium px-6'>
                {t('our_offers')}
              </h2>
              <JobListContext.Provider value={{ state, dispatch }}>
                <CompanyJobsList
                  jobs={jobListData}
                  jobsLocations={jobsLocations}
                  totalCount={totalJobsCount}
                  company={company}
                  domainLang={domainLang}
                />
              </JobListContext.Provider>
            </div>
          )}

          {(company.currentLayout === 'basic' ||
            (company.currentLayout === 'brand' &&
              jobListData.length === 0)) && (
            <JobListContext.Provider value={{ state, dispatch }}>
              <DynamicOtherJobsList
                exclude={jobListData}
                ref={sectionRefs[5]}
                company={company}
              />
            </JobListContext.Provider>
          )}
        </div>
      </main>
    </div>
  )
}

export default CompanyTemplate
